import { createContext, useEffect, useReducer, useContext } from 'react';
import { useQueryClient } from 'react-query';
import type { FC } from 'react';
import api from 'src/config/api';
import * as authService from 'src/services/authService';
import { User } from 'src/types/user';
import {
  UserPermissions,
  UserCapabilityNames,
  InstanceCapabilityNames,
  canLogin,
  updatePermissions,
} from 'src/utils/permissions';
import { isOnMaintenance } from 'src/utils/maintenance';
import { restoreNameSpaces } from 'src/utils/translations';
import { Instance, StaticProfileSettings } from 'src/types/instance';
import { ErrorCode } from 'src/constants/exceptions';
import { permissionsObjectToArray } from 'src/utils/userUtils';
import {
  setUser as setAmplitudeUser,
  clearUser as clearAmplitudeUser,
  logAmplitudeEvent,
} from 'src/config/amplitude';
import {
  setUser as setSentryUser,
  clearUser as clearSentryUser,
} from 'src/config/sentry';
import {
  EventName,
  LoginTypes,
} from 'src/types/amplitude';
import { AxiosResponse } from 'axios';
import {
  LoginData,
  LoginOTPData,
  AzureLoginData,
  OktaLoginData,
  LoginResponse,
  GoogleLoginData,
  TokenLoginResponse,
} from 'src/types/login';
import { clearClarityUser, setClarityUser } from 'src/config/clarity';

type State = {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  instance: Instance | null;
  permissions: string[];
  accessToken: string | null;
};

type AuthContextValue = State & {
  platform: 'JWT';
  login: (loginData: LoginData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  loginOTP: (loginData: LoginOTPData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  loginSaml: (loginData: string) => Promise<AxiosResponse<TokenLoginResponse>>;
  loginNewCommunity: (loginData: LoginData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  azureLogin: (loginData: AzureLoginData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  oktaLogin: (loginData: OktaLoginData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  googleLogin: (loginData: GoogleLoginData) => (
    Promise<AxiosResponse<LoginResponse>>
  );
  updatePassword: (
    token: string,
    id: number,
    password: string,
    loginData: Pick<LoginData, 'instanceId' | 'employeeInternalId'>
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateInstance: (instance: Instance) => void;
  updateStaticProfileSettings: (settings: StaticProfileSettings) => void;
  changeLanguage: (user: User) => void;
  changeLogoInstance: (logo: string) => void;
  acceptedTermsAndConditions: () => void;
  updateUserPermissions: (permissions: Record<UserCapabilityNames, boolean>) => void;
  updateInstancePermissions: (permissions: Record<InstanceCapabilityNames, boolean>) => void;
  updateAcknowledgementsLeftToGive: ({ acknowledgementsToGiveLeft }) => void;
};

type AuthProviderProps = {
};

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
    instance: Instance | null;
    accessToken: string | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    isAuthenticated: boolean;
    user: User;
    instance: Instance;
    accessToken: string;
  };
};

type ChangeLogoInstanceAction = {
  type: 'CHANGE_LOGO_INSTANCE';
  payload: {
    logo: string;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type AcceptedTermsAndConditionsAction = {
  type: 'ACCEPTED_TEMS_AND_CONDITIONS'
};

type UpdateInstanceAction = {
  type: 'UPDATE_INSTANCE';
  payload: {
    instance: Instance;
  };
};

type UpdateStaticProfileSettingsAction = {
  type: 'UPDATE_STATIC_PROFILE_SETTINGS';
  payload: {
    settings: StaticProfileSettings;
  };
};

type UpdateUserPermissionsAction = {
  type: 'UPDATE_USER_PERMISSIONS';
  payload: {
    permissions: Record<UserPermissions, boolean>;
  };
};

type UpdatePermissionsAction = {
  type: 'UPDATE_PERMISSIONS';
  payload: {
    permissions: string[];
  };
};

type ChangeLanguage = {
  type: 'CHANGE_LANGUAGE';
  payload: {
    user: User;
  };
};

type UpdateAcknowledgementsLeftToGiveAction = {
  type: 'UPDATE_ACKNOWLEDGEMENTS_LEFT_TO_GIVE';
  payload: {
    acknowledgementsToGiveLeft: number;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | UpdateInstanceAction
  | UpdateStaticProfileSettingsAction
  | UpdateUserPermissionsAction
  | UpdatePermissionsAction
  | ChangeLanguage
  | AcceptedTermsAndConditionsAction
  | ChangeLogoInstanceAction
  | UpdateAcknowledgementsLeftToGiveAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  instance: null,
  permissions: [],
  accessToken: null,
};

const setSession = (accessToken: string | null): void => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }
  else {
    localStorage.removeItem('accessToken');
    delete api.defaults.headers.common.Authorization;
  }
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user, instance, accessToken } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      instance,
      accessToken,
      permissions: permissionsObjectToArray(user?.permissions),
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { isAuthenticated, user, instance, accessToken } = action.payload;

    return {
      ...state,
      isAuthenticated,
      user,
      instance,
      accessToken,
      permissions: permissionsObjectToArray(user?.permissions),
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
    instance: null,
    permissions: [],
  }),
  UPDATE_STATIC_PROFILE_SETTINGS: (
    state: State,
    action: UpdateStaticProfileSettingsAction
  ): State => {
    const { settings } = action.payload;

    return {
      ...state,
      instance: {
        ...state.instance,
        ...settings,
      },
    };
  },
  UPDATE_INSTANCE: (state: State, action: UpdateInstanceAction): State => {
    const { instance } = action.payload;

    return {
      ...state,
      instance,
    };
  },
  UPDATE_USER_PERMISSIONS: (state: State, action: UpdateUserPermissionsAction): State => {
    const { permissions } = action.payload;

    return {
      ...state,
      user: {
        ...state.user,
        permissions,
      },
    };
  },
  UPDATE_ACKNOWLEDGEMENTS_LEFT_TO_GIVE: (
    state: State,
    action: UpdateAcknowledgementsLeftToGiveAction,
  ): State => {
    const { acknowledgementsToGiveLeft } = action.payload;

    return {
      ...state,
      user: {
        ...state.user,
        acknowledgementsToGiveLeft,
      },
    };
  },
  UPDATE_PERMISSIONS: (state: State, action: UpdatePermissionsAction): State => {
    const { permissions } = action.payload;

    return {
      ...state,
      permissions,
    };
  },
  CHANGE_LANGUAGE: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      user: {
        ...state.user,
        language: user.language,
      },
    };
  },
  CHANGE_LOGO_INSTANCE: (state: State, action: ChangeLogoInstanceAction): State => {
    const { logo } = action.payload;

    return {
      ...state,
      instance: {
        ...state.instance,
        logo,
      },
    };
  },
  ACCEPTED_TEMS_AND_CONDITIONS: (state: State): State => ({
    ...state,
    user: {
      ...state.user,
      lastTermsAndConditionsAccepted: true,
    },
  }),
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: null,
  login: null,
  loginOTP: null,
  loginSaml: null,
  loginNewCommunity: null,
  azureLogin: null,
  oktaLogin: null,
  googleLogin: null,
  updatePassword: null,
  logout: null,
  changeLanguage: null,
  updateInstance: null,
  updateStaticProfileSettings: null,
  changeLogoInstance: null,
  acceptedTermsAndConditions: null,
  updateUserPermissions: null,
  updateInstancePermissions: null,
  updateAcknowledgementsLeftToGive: null,
  permissions: [],
});

export const AuthProvider: FC<React.PropsWithChildren<AuthProviderProps>> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        let accessToken = null;

        if (location.pathname.includes('org-chart-mobile') ||
          location.pathname.includes('events-nemak-mobile')) {
          accessToken = location.pathname.split('/')[2];
        }
        else {
          accessToken = window.localStorage.getItem('accessToken');
        }

        if (!isOnMaintenance() && accessToken) {
          setSession(accessToken);
          const response = await authService.tokenLogin();
          const { user, instance } = response.data;
          setAmplitudeUser(user, instance);
          setSentryUser(user, instance);
          setClarityUser(user, instance);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
              instance,
              accessToken,
            },
          });
        }
        else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              instance: null,
              accessToken: null,
            },
          });
        }
      }
      catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            instance: null,
            accessToken: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const loginOTP = async (loginData: LoginOTPData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.loginOTP(loginData);
    const { user, instance, token } = response.data;

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.OTP,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const loginSaml = async (bearerToken) => {
    setSession(bearerToken);

    const response = await authService.tokenLogin();
    const { user, instance, token } = response.data;

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.REGULAR,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: bearerToken,
      },
    });

    return response;
  };

  const login = async (loginData: LoginData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.login(loginData);
    const { token, user, instance } = response.data;
    const permissions = permissionsObjectToArray(user.permissions);

    if (!canLogin(permissions)) {
      throw new Error(ErrorCode.NOT_ENOUGH_PERMISSIONS);
    }

    if (user.hasPasswordChanged) {
      setSession(token);
      setAmplitudeUser(user, instance);
      setSentryUser(user, instance);
      setClarityUser(user, instance);
      logAmplitudeEvent(EventName.USER_LOGIN, {
        userId: user?.id,
        login: LoginTypes.REGULAR,
      });
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: user.hasPasswordChanged,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const loginNewCommunity = async (loginData: LoginData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.login(loginData);
    const { token, user, instance } = response.data;
    const permissions = permissionsObjectToArray(user.permissions);

    if (!canLogin(permissions)) {
      throw new Error(ErrorCode.NOT_ENOUGH_PERMISSIONS);
    }

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.REGULAR,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const azureLogin = async (loginData: AzureLoginData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.azureLogin(loginData);
    const { token, user, instance } = response.data;

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.AZURE,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const oktaLogin = async (loginData: OktaLoginData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.oktaLogin(loginData);
    const { token, user, instance } = response.data;

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.OKTA,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const googleLogin = async (loginData: GoogleLoginData): Promise<AxiosResponse<LoginResponse>> => {
    const response = await authService.googleLogin(loginData);
    const { token, user, instance } = response.data;

    setSession(token);
    setAmplitudeUser(user, instance);
    setSentryUser(user, instance);
    setClarityUser(user, instance);
    logAmplitudeEvent(EventName.USER_LOGIN, {
      userId: user?.id,
      login: LoginTypes.GOOGLE,
    });

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user,
        instance,
        accessToken: token,
      },
    });

    return response;
  };

  const updatePassword = async (
    token: string,
    id: number,
    password: string,
    loginData: Pick<LoginData, 'instanceId' | 'employeeInternalId'>,
  ): Promise<void> => {
    await authService.updatePassword(id, password, token);
    login({ ...loginData, password });
  };

  const logout = async (): Promise<void> => {
    queryClient.clear();
    setSession(null);
    clearAmplitudeUser();
    clearSentryUser();
    clearClarityUser();
    dispatch({ type: 'LOGOUT' });
    restoreNameSpaces();
  };

  const changeLanguage = (user: User) => {
    dispatch({ type: 'CHANGE_LANGUAGE', payload: { user } });
  };

  const changeLogoInstance = (logo: string) => {
    dispatch({ type: 'CHANGE_LOGO_INSTANCE', payload: { logo } });
  };

  const acceptedTermsAndConditions = () => {
    dispatch({ type: 'ACCEPTED_TEMS_AND_CONDITIONS' });
  };

  const updateStaticProfileSettings = (settings: StaticProfileSettings) => {
    dispatch({ type: 'UPDATE_STATIC_PROFILE_SETTINGS', payload: { settings } });
  };

  const updateInstance = (instance: Instance) => {
    dispatch({ type: 'UPDATE_INSTANCE', payload: { instance } });
  };

  const updateUserPermissions = (permissions: Record<UserCapabilityNames, boolean>) => {
    const userPermissions = updatePermissions(state?.user?.permissions, permissions);

    dispatch({
      type: 'UPDATE_USER_PERMISSIONS',
      payload: { permissions: userPermissions },
    });

    dispatch({
      type: 'UPDATE_PERMISSIONS',
      payload: { permissions: permissionsObjectToArray(userPermissions) },
    });
  };

  const updateInstancePermissions = (permissions: Record<InstanceCapabilityNames, boolean>) => {
    const userPermissions = updatePermissions(state?.user?.permissions, permissions);

    dispatch({
      type: 'UPDATE_PERMISSIONS',
      payload: { permissions: permissionsObjectToArray(userPermissions) },
    });
  };

  const updateAcknowledgementsLeftToGive = ({ acknowledgementsToGiveLeft }) => {
    dispatch({
      type: 'UPDATE_ACKNOWLEDGEMENTS_LEFT_TO_GIVE',
      payload: { acknowledgementsToGiveLeft },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        loginOTP,
        loginSaml,
        loginNewCommunity,
        azureLogin,
        oktaLogin,
        googleLogin,
        updatePassword,
        logout,
        updateStaticProfileSettings,
        updateInstance,
        updateUserPermissions,
        updateInstancePermissions,
        changeLanguage,
        changeLogoInstance,
        acceptedTermsAndConditions,
        updateAcknowledgementsLeftToGive,
        permissions: state.permissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
